import React from 'react';
import { CommonUtils, ProjectDetail } from './commonUtils';
import { datetimeLocalToIsoUtc } from './dateTime';

// 공통 타입 정의
export type VoucherType = 'PREPAID' | 'COUPON' | 'BIZ' | 'EVENT';
export type VoucherStatus = 'ACTIVE' | 'INACTIVE';
export type DiscountTarget = 'PRODUCT' | 'PAYMENT' | 'REWARD';
export type DiscountType = 'AMOUNT' | 'PERCENTAGE' | 'FIXED';
export type ValidType = 0 | 1; // 0: 기간, 1: 지정일

// 폼 데이터 인터페이스
export interface FormData {
  brandCode: string;
  productCode: string;
  masterCode: string;
  status: VoucherStatus;
  type: VoucherType;
  name: string;
  price: number;
  balance: number;
  discountTarget: DiscountTarget;
  discountType: DiscountType;
  discountValue: number;
  maxDiscount: number;
  minPayment: number;
  validType: ValidType;
  validPeriod: number;
  validUntil: string;
  issueLimit: number;
  memberLimit: number;
  comment: string;
  description: string;
  notice: string;
}

export interface FormErrors {
  brandCode?: string;
  productCode?: string;
  masterCode?: string;
  status?: string;
  type?: string;
  name?: string;
  price?: string;
  balance?: string;
  discountTarget?: string;
  discountType?: string;
  discountValue?: string;
  maxDiscount?: string;
  minPayment?: string;
  validType?: string;
  validPeriod?: string;
  validUntil?: string;
  issueLimit?: string;
  memberLimit?: string;
  comment?: string;
  description?: string;
  notice?: string;
}

// 공통 유틸리티 함수들
export class VoucherFormUtils {
  // 폼 검증 함수 (기본)
  static validateForm(formData: FormData): FormErrors {
    return this.validateCreateForm(formData);
  }

  // 교환권 코드 허용 문자: 영문 대소문자, 숫자, 언더스코어(_), 하이픈(-)
  static readonly CODE_PATTERN = /^[a-zA-Z0-9_-]*$/;

  // 교환권 생성 폼 검증
  static validateCreateForm(formData: FormData): FormErrors {
    const newErrors: FormErrors = {};

    // 필수 필드 검증
    if (!formData.masterCode.trim()) {
      newErrors.masterCode = '교환권 코드를 입력해주세요.';
    } else if (!VoucherFormUtils.CODE_PATTERN.test(formData.masterCode)) {
      newErrors.masterCode = '교환권 코드는 영문 대소문자, 숫자, 언더스코어(_), 하이픈(-)만 사용할 수 있습니다.';
    } else if (formData.masterCode.length > 20) {
      newErrors.masterCode = '교환권 코드는 20자 이하로 입력해주세요.';
    }

    if (!formData.name.trim()) {
      newErrors.name = '교환권 명칭을 입력해주세요.';
    } else if (formData.name.length > 20) {
      newErrors.name = '교환권 명칭은 20자 이하로 입력해주세요.';
    }

    if (formData.price < 0) {
      newErrors.price = '판매 금액은 0 이상이어야 합니다.';
    }

    if (formData.balance < 0) {
      newErrors.balance = '사용 가능 잔액은 0 이상이어야 합니다.';
    }

    if (formData.discountValue < 0) {
      newErrors.discountValue = '할인 금액은 0 이상이어야 합니다.';
    }

    if (formData.maxDiscount < 0) {
      newErrors.maxDiscount = '최대 할인 금액은 0 이상이어야 합니다.';
    }

    if (formData.minPayment < 0) {
      newErrors.minPayment = '최소 결제 금액은 0 이상이어야 합니다.';
    }

    if (formData.validType === 0 && formData.validPeriod <= 0) {
      newErrors.validPeriod = '유효기간은 1일 이상이어야 합니다.';
    }

    if (formData.validType === 1 && !String(formData.validUntil || '').trim()) {
      newErrors.validUntil = '유효 일시를 입력해주세요.';
    }

    if (formData.issueLimit < 0) {
      newErrors.issueLimit = '발행 제한은 0 이상이어야 합니다.';
    }

    if (formData.memberLimit < 0) {
      newErrors.memberLimit = '멤버 제한은 0 이상이어야 합니다.';
    }

    if (formData.comment && formData.comment.length > 50) {
      newErrors.comment = '짧은 소개는 50자 이하로 입력해주세요.';
    }

    return newErrors;
  }

  // 기본 폼 데이터
  static getDefaultFormData(): FormData {
    return {
      brandCode: '',
      productCode: '',
      masterCode: '',
      status: 'ACTIVE',
      type: 'COUPON',
      name: '',
      price: 0,
      balance: 0,
      discountTarget: 'PAYMENT',
      discountType: 'AMOUNT',
      discountValue: 0,
      maxDiscount: 0,
      minPayment: 0,
      validType: 0,
      validPeriod: 30,
      validUntil: '',
      issueLimit: 0,
      memberLimit: 0,
      comment: '',
      description: '',
      notice: ''
    };
  }

  // 프로젝트 정보 로드
  static loadProjectDetail(): any {
    return CommonUtils.loadProjectDetail();
  }

  // 입력 변경 처리
  static handleInputChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    setFormData: React.Dispatch<React.SetStateAction<FormData>>,
    setErrors: React.Dispatch<React.SetStateAction<FormErrors>>
  ) {
    const { name, value, type } = e.target;
    // 교환권 코드: 영문 대소문자, 숫자, 언더스코어(_), 하이픈(-)만 허용
    const nextValue = name === 'masterCode'
      ? String(value).replace(/[^a-zA-Z0-9_-]/g, '')
      : type === 'number'
        ? Number(value)
        : value;
    setFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));

    // 에러 제거
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[name as keyof FormErrors];
      return newErrors;
    });
  }

  // 폼 제출 처리
  static async handleCreateSubmit(
    e: React.FormEvent,
    formData: FormData,
    validateForm: () => boolean,
    setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
    setSubmitError: React.Dispatch<React.SetStateAction<string | null>>,
    getProjectCode: () => string | null,
    createVoucher: (projectCode: string, data: any) => Promise<any>,
    navigate: (path: string) => void
  ) {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const projectCode = getProjectCode();
      if (!projectCode) {
        throw new Error('프로젝트 정보를 찾을 수 없습니다.');
      }

      const requestData = {
        brandCode: formData.brandCode?.trim() || undefined,
        productCode: formData.productCode.trim() || null,
        masterCode: formData.masterCode,
        status: formData.status,
        type: formData.type,
        name: formData.name,
        price: formData.price,
        balance: formData.balance,
        discountTarget: formData.discountTarget,
        discountType: formData.discountType,
        discountValue: formData.discountValue,
        maxDiscount: formData.maxDiscount || undefined,
        minPayment: formData.minPayment || undefined,
        validType: formData.validType,
        validPeriod: formData.validType === 0 ? formData.validPeriod || undefined : undefined,
        validUntil:
          formData.validType === 1 && String(formData.validUntil || '').trim()
            ? datetimeLocalToIsoUtc(String(formData.validUntil).trim())
            : undefined,
        issueLimit: formData.issueLimit,
        memberLimit: formData.memberLimit || undefined,
        comment: formData.comment || undefined,
        description: formData.description || undefined,
        notice: formData.notice || undefined
      };

      const response = await createVoucher(projectCode, requestData);
      
      if (response.success) {
        alert('교환권이 성공적으로 생성되었습니다.');
        navigate('/vouchers');
      } else {
        throw new Error(response.message || '교환권 생성에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('교환권 생성 오류:', error);
      // 서버에서 보낸 에러 메시지 우선 사용
      const displayMessage = error?.message || CommonUtils.getNetworkErrorMessage(error) || '교환권 생성 중 오류가 발생했습니다.';
      setSubmitError(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  }

  // 정렬 핸들러
  static handleSort(
    field: string,
    currentSortField: string,
    currentSortOrder: string,
    setSortField: React.Dispatch<React.SetStateAction<string>>,
    setSortOrder: React.Dispatch<React.SetStateAction<string>>
  ) {
    return CommonUtils.handleSort(field, currentSortField, currentSortOrder, setSortField, setSortOrder);
  }

  // 모바일 사이드바 리사이즈 훅
  static useMobileSidebarResize(setIsMobileSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>) {
    React.useEffect(() => {
      const handleResize = () => {
        if (window.innerWidth >= 1024) {
          setIsMobileSidebarOpen(false);
        }
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [setIsMobileSidebarOpen]);
  }

  // 뷰 타입 변경 핸들러
  static handleViewTypeChange(
    newViewType: string,
    setViewType: React.Dispatch<React.SetStateAction<string>>,
    location: any,
    navigate: any
  ) {
    return CommonUtils.handleViewTypeChange(newViewType, setViewType, location, navigate, '/vouchers');
  }

  // 교환권 생성 페이지로 이동
  static handleVoucherCreate(navigate: (path: string) => void) {
    navigate('/vouchers/create');
  }

  // 교환권 수정 페이지로 이동
  static handleVoucherEdit(navigate: (path: string) => void, voucherCode: string, currentPage: number, pageSize: number) {
    navigate(`/vouchers/${encodeURIComponent(voucherCode)}/update?page=${currentPage}&size=${pageSize}`);
  }

  // 교환권 삭제 처리
  static handleVoucherDelete(voucherCode: string, onDelete?: (code: string) => void) {
    return CommonUtils.handleDelete(voucherCode, '교환권', onDelete);
  }

  // 교환권 상태 배지 생성
  static getStatusBadge(status: string) {
    return CommonUtils.getStatusBadge(status, 'voucher');
  }

  // 교환권 타입 정보 가져오기
  static getVoucherTypeInfo(type: string) {
    const typeMap: { [key: string]: { label: string; color: string; icon: string } } = {
      'PREPAID': { label: '선불권', color: 'bg-blue-100 text-blue-800', icon: 'DollarSign' },
      'COUPON': { label: '쿠폰', color: 'bg-green-100 text-green-800', icon: 'Percent' },
      'BIZ': { label: '기업용', color: 'bg-purple-100 text-purple-800', icon: 'Shield' },
      'EVENT': { label: '이벤트', color: 'bg-orange-100 text-orange-800', icon: 'Star' }
    };
    return typeMap[type] || { label: '알 수 없음', color: 'bg-gray-100 text-gray-800', icon: 'Ticket' };
  }


  // 폼 제출 처리 (교환권 수정)
  static async handleUpdateSubmit(
    e: React.FormEvent,
    formData: FormData,
    validateForm: () => boolean,
    setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
    setSubmitError: React.Dispatch<React.SetStateAction<string | null>>,
    setHasChanges: React.Dispatch<React.SetStateAction<boolean>>,
    setOriginalData: React.Dispatch<React.SetStateAction<FormData | null>>,
    getProjectCode: () => string,
    voucherCode: string,
    updateVoucher: (projectCode: string, voucherCode: string, data: any) => Promise<any>,
    navigate: any,
    location: any
  ) {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const projectCode = getProjectCode();
      const voucherData = {
        brandCode: formData.brandCode?.trim() || undefined,
        productCode: formData.productCode || null,
        masterCode: formData.masterCode,
        status: formData.status,
        type: formData.type,
        name: formData.name,
        price: formData.price,
        balance: formData.balance,
        discountTarget: formData.discountTarget,
        discountType: formData.discountType,
        discountValue: formData.discountValue,
        maxDiscount: formData.maxDiscount,
        minPayment: formData.minPayment,
        validType: formData.validType,
        validPeriod: formData.validType === 0 ? formData.validPeriod : undefined,
        validUntil:
          formData.validType === 1 && String(formData.validUntil || '').trim()
            ? datetimeLocalToIsoUtc(String(formData.validUntil).trim())
            : undefined,
        issueLimit: formData.issueLimit,
        memberLimit: formData.memberLimit,
        comment: formData.comment || undefined,
        description: formData.description || undefined,
        notice: formData.notice || undefined
      };

      const result = await updateVoucher(projectCode, voucherCode, voucherData);
      
      if (result.success) {
        setHasChanges(false);
        setOriginalData({...formData});
        // 교환권 목록으로 이동 (페이징 정보 유지)
        const searchParams = new URLSearchParams(location.search);
        const page = searchParams.get('page') || '0';
        const size = searchParams.get('size') || '20';
        navigate(`/vouchers?page=${page}&size=${size}`);
      } else {
        setSubmitError(result.message || '교환권 수정에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('교환권 수정 오류:', error);
      // 서버에서 보낸 에러 메시지 우선 사용
      const displayMessage = error?.message || CommonUtils.getNetworkErrorMessage(error) || '교환권 수정 중 오류가 발생했습니다.';
      setSubmitError(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  }
}